import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { getAuth } from "firebase/auth";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/react";
import { ReactComponent as EditIcon } from "../../assets/icons/EditIcon.svg";
import LoadingSpinner from "../LoadingSpinner";
import EmptyState from "../EmptyState";
import { useNavigate, useLocation } from "react-router-dom";
import { useCotizaciones, aPesos } from "../../services/cotizaciones";
const divisas = {
Pesos: "$",
"Dólares": "US$",
Reales: "R$",
Euros: "€",
};
const categorias = [
"Activos",
"Almacén",
"Depósito",
"Enología",
"Finca",
"Insumos",
"Utilitarios",
];
const unidadesAbreviaturas = {
Unidad: "u",
Kilos: "kg",
Gramos: "g",
Litro: "lts",
Centilitros: "cl",
Quintales: "q",
Mililitros: "ml",
Hectolitros:'hl'
};
const Tooltip = ({ content, children, delay = 250, offsetX = 0, offsetY = 0, centrado = false }) => {
const [isHovered, setIsHovered] = useState(false);
const [coords, setCoords] = useState({ top: 0, left: 0 });
const ref = React.useRef();
const timeoutRef = React.useRef();
const handleMouseEnter = () => {
const rect = ref.current.getBoundingClientRect();
setCoords({
// 'centrado' se usa para el tooltip de embotellados: con contenido largo
// (partidas usadas), pegarlo abajo del elemento lo cortaba contra el
// borde de la pantalla. El resto de los tooltips sigue colgando abajo,
// como siempre.
top: centrado
? rect.top + rect.height / 2 + window.scrollY + offsetY
: rect.bottom + window.scrollY + 5 + offsetY,
left: rect.left + rect.width / 2 + window.scrollX + offsetX,
});
timeoutRef.current = setTimeout(() => {
setIsHovered(true);
}, delay);
};
const handleMouseLeave = () => {
clearTimeout(timeoutRef.current);
setIsHovered(false);
};
return (
<>
<div
ref={ref}
style={{ display: 'inline-block' }}
onMouseEnter={handleMouseEnter}
onMouseLeave={handleMouseLeave}
>
{children}
</div>
{isHovered && (
<div
style={{
position: 'absolute',
top: coords.top,
left: coords.left,
transform: centrado ? 'translateY(-50%)' : undefined,
backgroundColor: '#f9fafd',
color: '#fff',
padding: '8px 12px',
borderRadius: '4px',
whiteSpace: 'normal',
zIndex: 1000,
fontSize: '14px',
minWidth:'200px',
pointerEvents: 'none',
boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
}}
>
{content}
</div>
)}
</>
);
};
// Edición de la ficha de un ítem de stock. NO toca la cantidad: eso se ajusta
// solo por conteo físico desde Inventario, que tiene su propia auditoría.
const StockEditModal = ({ item, onClose, onSuccess }) => {
const [form, setForm] = useState({
categoria: item.categoria || '',
tipoUnidad: item.tipoUnidad || '',
tipoDivisa: item.tipoDivisa || 'Pesos',
precioUnidad: item.precioUnidad ?? '',
existenciasMinimas: item.existenciasMinimas ?? '',
existenciasMaximas: item.existenciasMaximas ?? '',
diasConteo: item.diasConteo ?? '',
});
const [guardando, setGuardando] = useState(false);
const [error, setError] = useState('');
const opcionesConteo = { 'Diario': 1, 'Semanal': 7, 'Mensual': 30, 'Trimestral': 90, 'Semestral': 180, 'Anual': 365 };
const handleChange = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }));
const handleGuardar = async (e) => {
e.preventDefault();
setGuardando(true);
setError('');
try {
const user = getAuth().currentUser;
if (!user) throw new Error('Debés iniciar sesión.');
const idToken = await user.getIdToken();
const res = await fetch(`https://backend-288175208699.europe-west1.run.app/editar-stock/${encodeURIComponent(item.id)}`, {
method: 'PUT',
headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
body: JSON.stringify({
categoria: form.categoria,
tipoUnidad: form.tipoUnidad,
tipoDivisa: form.tipoDivisa,
precioUnidad: form.precioUnidad === '' ? null : Number(form.precioUnidad),
existenciasMinimas: form.existenciasMinimas === '' ? null : Number(form.existenciasMinimas),
existenciasMaximas: form.existenciasMaximas === '' ? null : Number(form.existenciasMaximas),
diasConteo: form.diasConteo === '' ? null : Number(form.diasConteo),
}),
});
const data = await res.json();
if (!res.ok) throw new Error(data.error || 'No se pudo guardar.');
onSuccess();
} catch (err) {
setError(err.message);
} finally {
setGuardando(false);
}
};
const renderListbox = (valor, opciones, onChange, placeholder) => (
<Listbox value={valor} onChange={onChange}>
{({ open }) => (
<>
<ListboxButton className={`custom-listbox-button ${open ? 'open' : ''} ${valor ? 'selected' : 'placeholder'}`}>
{valor || placeholder}
<span className="custom-arrow-container"><span className="custom-arrow"></span></span>
</ListboxButton>
<ListboxOptions className="custom-listbox-options">
{opciones.map(op => (
<ListboxOption key={op} value={op} className="custom-listbox-option">{op}</ListboxOption>
))}
</ListboxOptions>
</>
)}
</Listbox>
);
return (
<div className="container" style={{ margin: '0 10px' }}>
<div className="formtitle-container">
<h1 className="formtitle">Editar ficha de stock</h1>
<div className='remove-button' onClick={onClose} style={{ margin: '0 0 0 auto' }}>&times;</div>
</div>
<hr className="divider" />
<form className="form" onSubmit={handleGuardar}>
<div className="columns-container">
<div className="column long">
<label className='date-label'>Ítem</label>
<div className="input-group"><input className="input" value={item.nombre || item.id} disabled /></div>
<label className='date-label'>Categoría</label>
<div className="input-group">{renderListbox(form.categoria, categorias, v => handleChange('categoria', v), 'Categoría')}</div>
<label className='date-label'>Tipo de unidad</label>
<div className="input-group">{renderListbox(form.tipoUnidad, Object.keys(unidadesAbreviaturas), v => handleChange('tipoUnidad', v), 'Tipo de unidad')}</div>
<label className='date-label'>Conteo de inventario</label>
<div className="input-group">
{renderListbox(
Object.keys(opcionesConteo).find(k => opcionesConteo[k] === Number(form.diasConteo)) || '',
Object.keys(opcionesConteo),
v => handleChange('diasConteo', opcionesConteo[v]),
'Realizar inventario'
)}
</div>
</div>
<div className="column long">
<label className='date-label'>Precio unitario</label>
<div className="input-group number">
<input type="number" step="0.01" className="input" value={form.precioUnidad}
onChange={(e) => handleChange('precioUnidad', e.target.value)} placeholder="Precio unitario" />
<span className="unit">{divisas[form.tipoDivisa] || '$'}</span>
</div>
<label className='date-label'>Moneda</label>
<div className="input-group">{renderListbox(form.tipoDivisa, Object.keys(divisas), v => handleChange('tipoDivisa', v), 'Moneda')}</div>
<label className='date-label'>Existencias mínimas</label>
<div className="input-group number">
<input type="number" className="input" value={form.existenciasMinimas}
onChange={(e) => handleChange('existenciasMinimas', e.target.value)} placeholder="Existencias mínimas" />
<span className="unit">{unidadesAbreviaturas[form.tipoUnidad] || ''}</span>
</div>
<label className='date-label'>Existencias máximas</label>
<div className="input-group number">
<input type="number" className="input" value={form.existenciasMaximas}
onChange={(e) => handleChange('existenciasMaximas', e.target.value)} placeholder="Existencias máximas" />
<span className="unit">{unidadesAbreviaturas[form.tipoUnidad] || ''}</span>
</div>
<p style={{ fontSize: '12px', color: '#888', fontFamily: 'Effra' }}>
La cantidad en stock no se edita acá: se ajusta por conteo físico desde Inventario.
</p>
{error && <p style={{ color: '#e65c5c', fontSize: '13px', fontFamily: 'Effra' }}>{error}</p>}
<div className="button-container">
<hr style={{ border: "0", borderTop: "1px solid #eaeef5", margin: "0px 0 0px" }} />
<button type="submit" className="button" disabled={guardando} style={{ width: '175px' }}>
<span className="button-text">{guardando ? 'Guardando...' : 'Guardar cambios'}</span>
</button>
</div>
</div>
</div>
</form>
</div>
);
};
const Stock = () => {
const [stockItems, setStockItems] = useState([]);
const [itemParaEditar, setItemParaEditar] = useState(null);
const [loading, setLoading] = useState(true);
const location = useLocation();
// Al crear un vino se navega acá indicando en qué categoría cayó (ej.
// "Depósito"), para no aterrizar siempre en "Activos" por defecto.
const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(
location.state?.categoriaInicial || "Activos"
);
// Desde el detalle de un TRAZ con vino comprado se puede venir acá para
// ver ese ítem puntual — se resalta y se hace scroll hasta él. El resaltado
// es un flash: aparece y se apaga solo después de un segundo, no se queda
// prendido para siempre.
const itemDestacado = location.state?.itemDestacado || null;
const [resaltadoActivo, setResaltadoActivo] = useState(!!itemDestacado);
// El modal de edición se abre empujando 'editandoId' al historial (ver el
// botón de editar más abajo). Cuando esta entrada deja de estar activa —
// por la flecha "atrás" del navegador, o por el navigate(-1) al cerrar/
// guardar — este efecto cierra el modal solo, en vez de depender de que
// alguien llame a setItemParaEditar(null) a mano en cada lugar.
useEffect(() => {
const id = location.state?.editandoId;
if (!id) {
setItemParaEditar(null);
return;
}
setItemParaEditar(prev => (prev?.id === id ? prev : stockItems.find(i => i.id === id) || prev));
}, [location.state, stockItems]);
useEffect(() => {
if (!itemDestacado) return;
const timer = setTimeout(() => setResaltadoActivo(false), 1200);
return () => clearTimeout(timer);
}, [itemDestacado]);
const [proveedores, setProveedores] = useState({});
// Partidas usadas por cada embotellado (vino sin etiquetar): viven en el doc
// padre 'embotellado', no en el item de stock, así que se buscan aparte.
const [partidasPorEmbotellado, setPartidasPorEmbotellado] = useState({});
const navigate = useNavigate();
const { cotizaciones } = useCotizaciones();
const [orden, setOrden] = useState({
columna: "",
direccion: "",
});
useEffect(() => {
const fetchProveedores = async () => {
try {
const snapshot = await getDocs(collection(db, "proveedores"));
const data = {};
snapshot.forEach((doc) => {
data[doc.id] = {
razonSocial: doc.data().razonSocial || "Sin razón social",
correoElectronico: doc.data().correoElectronico || "Sin email",
telefono: doc.data().telefono || "Sin teléfono",
ciudad: doc.data().ciudad || "Sin teléfono",
pais: doc.data().pais || "Sin teléfono",
};
});
setProveedores(data);
} catch (error) {
console.error("Error al traer proveedores:", error);
}
};
fetchProveedores();
}, []);
const fetchStock = async () => {
try {
const querySnapshot = await getDocs(collection(db, "stock"));
// Vinos demo (DEMO-SHOP-*/DEMO-CUPO-*) creados para probar Shop/Cupo
// asignado en fincalosastros — no son stock real, no deben verse acá.
const stockData = querySnapshot.docs
.filter((doc) => !doc.id.startsWith("DEMO-"))
.map((doc) => ({
id: doc.id,
...doc.data(),
}));
const sortedItems = stockData.sort((a, b) => {
const dateA = (a.ultimaActualizacion || a.fechaCreacion)?.toDate?.() || new Date(0);
const dateB = (b.ultimaActualizacion || b.fechaCreacion)?.toDate?.() || new Date(0);
return dateB - dateA;
});
setStockItems(sortedItems);
setLoading(false);
} catch (error) {
console.error("Error al traer datos de stock:", error);
setLoading(false);
}
};
useEffect(() => {
fetchStock();
}, []);
useEffect(() => {
const embotelladoIds = [...new Set(
stockItems.filter((i) => i.embotelladoId).map((i) => i.embotelladoId)
)].filter((id) => !partidasPorEmbotellado[id]);
if (embotelladoIds.length === 0) return;
const fetchPartidas = async () => {
try {
const embotelladoDocs = await Promise.all(
embotelladoIds.map((id) => getDoc(doc(db, "embotellado", id)))
);
const partidasUsadasPorId = {};
const refsVinificacion = new Set();
embotelladoDocs.forEach((snap, idx) => {
const partidas = snap.exists() ? (snap.data().partidas_usadas || []) : [];
partidasUsadasPorId[embotelladoIds[idx]] = partidas;
partidas.forEach((p) => {
if (p.origen === "vinificacion" && p.ref) refsVinificacion.add(p.ref);
});
});
// Las partidas propias (origen 'vinificacion') no traen bodega/nombre
// en 'partidas_usadas' -- eso vive en el doc de la partida misma.
const listaRefsVinificacion = [...refsVinificacion];
const vinificacionDocs = await Promise.all(
listaRefsVinificacion.map((ref) => getDoc(doc(db, "vinificacion", ref)))
);
const bodegaPorRef = {};
vinificacionDocs.forEach((snap, idx) => {
bodegaPorRef[listaRefsVinificacion[idx]] = snap.exists() ? snap.data().nombre_bodega : null;
});
const resultado = {};
Object.entries(partidasUsadasPorId).forEach(([embotelladoId, partidas]) => {
resultado[embotelladoId] = partidas.map((p) => ({
nombre: p.origen === "vinificacion" ? p.ref : (p.nombre_stock || p.ref),
bodega: p.origen === "vinificacion" ? (bodegaPorRef[p.ref] || "-") : (p.bodega || "-"),
cantidad: p.ya_embotellado
? `${p.cantidad_botellas ?? "-"} botellas`
: `${p.cantidad_hl ?? "-"} hl`,
}));
});
setPartidasPorEmbotellado((prev) => ({ ...prev, ...resultado }));
} catch (error) {
console.error("Error al traer partidas usadas de embotellados:", error);
}
};
fetchPartidas();
}, [stockItems, partidasPorEmbotellado]);
const handleSort = (columna) => {
const direccion =
orden.columna === columna && orden.direccion === "asc" ? "desc" : "asc";
setOrden({ columna, direccion });
};
if (loading) return <LoadingSpinner />;
const formatearFecha = (fecha) => {
if (!fecha) return "-";
let date;
if (fecha.toDate) {
date = fecha.toDate();
} else {
date = new Date(fecha);
}
return `El ${date.toLocaleDateString("es-AR")} a las ${date.toLocaleTimeString("es-AR", {
hour: "2-digit",
minute: "2-digit",
hour12: false,
})}`;
};
const formatearFechaEntrega = (fechaStr) => {
if (!fechaStr) return "-";
const date = new Date(fechaStr);
if (isNaN(date)) return fechaStr;
return date.toLocaleDateString("es-AR", {
day: "2-digit",
month: "2-digit",
year: "numeric",
});
};
const calcularDiasRestantes = (ultimoConteo, diasConteo) => {
if (!ultimoConteo || !diasConteo) return { text: "-", estado: "indefinido" };
const ultimaFecha = ultimoConteo.toDate ? ultimoConteo.toDate() : new Date(ultimoConteo);
const fechaProximoConteo = new Date(ultimaFecha);
fechaProximoConteo.setDate(ultimaFecha.getDate() + diasConteo);
const hoy = new Date();
const diferenciaMs = fechaProximoConteo.getTime() - hoy.getTime();
const diasRestantes = Math.ceil(diferenciaMs / (1000 * 60 * 60 * 24));
if (diasRestantes < 0) {
return { text: "Atrasado", estado: "atrasado" };
} else if (diasRestantes === 0) {
return { text: "Hoy", estado: "hoy" };
} else if (diasRestantes === 1) {
return { text: `En ${diasRestantes} día`, estado: "proximo" };
} else {
return { text: `En ${diasRestantes} días`, estado: "proximo" };
}
};
const getColorForStockLevel = (cantidad, min, max) => {
if (cantidad == null || min == null || max == null) {
return "black";
}
const currentQty = parseFloat(cantidad);
const minQty = parseFloat(min);
const maxQty = parseFloat(max);
const range = maxQty - minQty;
const quarter = range / 8;
if (currentQty < minQty || (currentQty >= minQty && currentQty <= minQty + quarter)) {
return "#e65c5c";
}
if (currentQty > maxQty || (currentQty >= maxQty - quarter && currentQty <= maxQty)) {
return "#e65c5c";
}
return "inherit";
};
const stockFiltrado =
categoriaSeleccionada === "Todos"
? stockItems
: stockItems.filter((item) => item.categoria === categoriaSeleccionada);
const stockParaTabla = [...stockFiltrado].sort((a, b) => {
// precioUnidad se compara pasando todo a Pesos: comparar los números crudos
// mezclaba artículos en Pesos/Dólares/Reales/Euros como si fueran la misma unidad.
if (orden.columna === "precioUnidad") {
const aPrecio = aPesos(a.precioUnidad, a.tipoDivisa, cotizaciones);
const bPrecio = aPesos(b.precioUnidad, b.tipoDivisa, cotizaciones);
return orden.direccion === "asc" ? aPrecio - bPrecio : bPrecio - aPrecio;
}
let aValue = a[orden.columna];
let bValue = b[orden.columna];
if (aValue === null || aValue === undefined) aValue = "";
if (bValue === null || bValue === undefined) bValue = "";
if (typeof aValue === "string") aValue = aValue.toLowerCase();
if (typeof bValue === "string") bValue = bValue.toLowerCase();
if (orden.columna.includes("fecha") || orden.columna.includes("Actualizacion")) {
const dateA = aValue?.toDate?.() || new Date(0);
const dateB = bValue?.toDate?.() || new Date(0);
return orden.direccion === "asc" ? dateA - dateB : dateB - dateA;
}
if (typeof aValue === "number" && typeof bValue === "number") {
return orden.direccion === "asc" ? aValue - bValue : bValue - aValue;
}
if (aValue < bValue) {
return orden.direccion === "asc" ? -1 : 1;
}
if (aValue > bValue) {
return orden.direccion === "asc" ? 1 : -1;
}
return 0;
});
return (
<div className="Trazabilidad">
<header className="trazabilidad-header">
<div className="header-content">
<h1>Stock</h1>
</div>
</header>
{itemParaEditar && (
<StockEditModal
item={itemParaEditar}
onClose={() => navigate(-1)}
onSuccess={() => { navigate(-1); fetchStock(); }}
/>
)}
{!itemParaEditar && (stockItems.length === 0 ? (
<EmptyState label="stock" />
) : (
<div className="trazabilidad-content">
<div className="section">
<h2>Recientes</h2>
{stockItems.length === 0 ? (
<p></p>
) : (
<div className="recent-grid">
{}
{stockItems.slice(0, 4).map((item) => (
<div
key={item.id}
className="document-card recent-card"
style={{
flexDirection: "column",
textAlign: "left",
alignItems: "start",
justifyContent: "start",
gap: "2px",
cursor: "pointer",
}}
onClick={() => setCategoriaSeleccionada(item.categoria)}
>
<span
className="doc-id"
style={{
display: "inline-block",
maxWidth: "250px",
overflow: "hidden",
textOverflow: "ellipsis",
whiteSpace: "nowrap",
textAlign: "left",
margin: 0,
}}
>{item.id || "Sin nombre"}
</span>
<p className="card-creator" style={{ textAlign: "left", margin: "0" }}>
{item.ultimaActualizacion
? item.ultimoModificador || "Sin modificador"
: item.creador || "Sin creador"}
</p>
<p className="card-date" style={{ textAlign: "left", margin: "0" }}>
{formatearFecha(item.ultimaActualizacion || item.fechaCreacion)}
</p>
</div>
))}
</div>
)}
</div>
<div className="section">
<ul className="compras-tabs">
{categorias.map((cat) => (
<li key={cat} className={categoriaSeleccionada === cat ? "active" : ""}>
<a
href="#"
onClick={(e) => {
e.preventDefault();
setCategoriaSeleccionada(cat);
}}
>
{cat}
</a>
</li>
))}
</ul>
{stockFiltrado.length === 0 ? (
<p></p>
) : (
<div className="table-container">
<table className="table-full" style={{ tableLayout: "fixed" }}>
<thead>
<tr>
{/* Columna del lapicito: sin encabezado, del ancho del ícono. */}
<th className="col-editar"></th>
<th style={{ fontFamily: "Effra", fontWeight: "600", fontSize: '14px', cursor: "pointer", width: "13.2%" }} onClick={() => handleSort("nombre")}>
Artículo{" "}
{orden.columna === "nombre" && (
<span className={`arrow ${orden.direccion}`}></span>
)}
</th>
<th style={{ fontFamily: "Effra", fontWeight: "600", fontSize: '14px', cursor: "pointer", width: "13.2%" }} onClick={() => handleSort("cantidadUnidades")}>
Existencias{" "}
{orden.columna === "cantidadUnidades" && (
<span className={`arrow ${orden.direccion}`}></span>
)}
</th>
<th style={{ fontFamily: "Effra", fontWeight: "600", fontSize: '14px', cursor: "pointer", width: "10%" }} onClick={() => handleSort("fechaUltimoConteo")}>
Inventario{" "}
{orden.columna === "fechaUltimoConteo" && (
<span className={`arrow ${orden.direccion}`}></span>
)}
</th>
<th style={{ fontFamily: "Effra", fontWeight: "600", fontSize: '14px', cursor: "pointer", width: "7%" }} onClick={() => handleSort("existenciasMaximas")}>
Máximo{" "}
{orden.columna === "existenciasMaximas" && (
<span className={`arrow ${orden.direccion}`}></span>
)}
</th>
<th style={{ fontFamily: "Effra", fontWeight: "600", fontSize: '14px', cursor: "pointer", width: "7%" }} onClick={() => handleSort("existenciasMinimas")}>
Mínimo{" "}
{orden.columna === "existenciasMinimas" && (
<span className={`arrow ${orden.direccion}`}></span>
)}
</th>
<th style={{ fontFamily: "Effra", fontWeight: "600", fontSize: '14px', cursor: "pointer", width: "13.2%" }} onClick={() => handleSort("ultimaActualizacion")}>
Última compra{" "}
{orden.columna === "ultimaActualizacion" && (
<span className={`arrow ${orden.direccion}`}></span>
)}
</th>
<th style={{ fontFamily: "Effra", fontWeight: "600", fontSize: '14px', cursor: "pointer", width: "13.2%" }} onClick={() => handleSort("precioUnidad")}>
Precio unitario{" "}
{orden.columna === "precioUnidad" && (
<span className={`arrow ${orden.direccion}`}></span>
)}
</th>
<th style={{ fontFamily: "Effra", fontWeight: "600", fontSize: '14px', cursor: "pointer", width: "10%" }} onClick={() => handleSort("descuentoAplicado")}>
Descuento{" "}
{orden.columna === "descuentoAplicado" && (
<span className={`arrow ${orden.direccion}`}></span>
)}
</th>
<th style={{ fontFamily: "Effra", fontWeight: "600", fontSize: '14px', cursor: "pointer", width: "13.2%" }} onClick={() => handleSort("proveedorAsociado")}>
Proveedor{" "}
{orden.columna === "proveedorAsociado" && (
<span className={`arrow ${orden.direccion}`}></span>
)}
</th>
</tr>
</thead>
<tbody>
{stockParaTabla.map((item) => {
// Los ítems generados por la cadena de trazabilidad (antes agrupados
// aparte en "Vinos y Frutas") ahora conviven en Insumos/Depósito con la
// mercadería comprada normal, pero conservan su propia pantalla de
// edición: lote de viticultura (id "VIT..."), partida de vinificación
// (id "VIN..."), línea de embotellado sin etiquetar (embotelladoId) o
// vino etiquetado/comprado a granel (trazabilidad_id).
const esLoteViticultura = item.id?.startsWith("VIT");
const esPartidaVinificacion = item.id?.startsWith("VIN");
const editPath = esLoteViticultura
? `/trazabilidad/viticultura/${item.id}/edit`
: esPartidaVinificacion
? `/trazabilidad/vinificacion/${item.id}/edit`
: item.embotelladoId
? `/trazabilidad/embotellado/${item.embotelladoId}/edit`
: item.embotellado_id
? `/trazabilidad/embotellado/${item.embotellado_id}/edit`
: null;
const editTitulo = esLoteViticultura
? "Editar lote"
: esPartidaVinificacion
? "Editar vinificación"
: editPath
? "Editar embotellado"
: "Editar ficha de stock";
const isSpecialId = item.id?.startsWith("TRAZ") || esLoteViticultura || esPartidaVinificacion;
const pathId = item.embotelladoId || (isSpecialId ? item.id : item.trazabilidad_id);
const displayId = item.embotelladoId || item.nombre || item.id || "-";
const shouldForceEtiqueta = !item.embotelladoId && (item.id?.startsWith("TRAZ") || !!item.trazabilidad_id);
return (
<tr
key={item.id}
className="table-row"
ref={item.id === itemDestacado ? (el) => el?.scrollIntoView({ behavior: 'smooth', block: 'center' }) : undefined}
style={{
cursor: 'default',
backgroundColor: item.id === itemDestacado && resaltadoActivo ? '#fff3cd' : 'transparent',
transition: 'background-color 0.8s ease-out',
}}>
<td className="col-editar">
<button
onClick={(e) => {
e.stopPropagation();
if (editPath) {
navigate(editPath);
return;
}
setItemParaEditar(item);
// Sin esto, la flecha "atrás" del navegador salta directo a la
// pantalla anterior a Stock (abrir el modal no dejaba rastro en el
// historial). Empujando esta entrada, "atrás" cierra el modal y te
// deja en Stock, como se espera.
navigate(location.pathname, { state: { ...location.state, editandoId: item.id } });
}}
title={editTitulo}
style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center" }}
>
<EditIcon style={{ width: "16px", height: "16px" }} />
</button>
</td>
<td style={{ textAlign: "start", cursor: "default", width: "13.2%" }}>
<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
<Tooltip centrado={!!item.embotelladoId} content={
item.embotelladoId ? (
  // Stock de vino embotellado sin etiquetar (generado por /api/embotellado):
  // header con el ID de la tirada + qué botella se usó, además de quién y
  // cuándo se creó (igual que el resto de los ítems de stock).
  <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
    <div><strong style={{ color: "#999", fontSize: "13px", fontFamily: "Effra", fontWeight: "normal", textTransform: "uppercase" }}>{item.embotelladoId}</strong></div>
    <div style={{ color: "#2c2c2c", fontSize: "13px", fontFamily: "Effra" }}>
      Botella:
      <p style={{ color: "#999", margin: "0", fontSize: "13px" }}>
        {[item.formaBotella, item.medidaBotella].filter(Boolean).join(" — ") || "-"}
      </p>
    </div>
    <div style={{ color: "#2c2c2c", fontSize: "13px", fontFamily: "Effra" }}>
      Botellas embotelladas:
      <p style={{ color: "#999", margin: "0", fontSize: "13px" }}>
        {item.cantidadUnidadesIniciales != null ? Number(item.cantidadUnidadesIniciales).toLocaleString("es-AR") : "-"}
      </p>
    </div>
    <div style={{ color: "#2c2c2c", fontSize: "13px", fontFamily: "Effra" }}>
      Partidas usadas:
      {(partidasPorEmbotellado[item.embotelladoId] || []).length === 0 ? (
        <p style={{ color: "#999", margin: "0", fontSize: "13px" }}>-</p>
      ) : (
        partidasPorEmbotellado[item.embotelladoId].map((p, idx) => (
          <p key={idx} style={{ color: "#999", margin: "0", fontSize: "13px" }}>
            {p.nombre} — {p.bodega} — {p.cantidad}
          </p>
        ))
      )}
    </div>
    <div style={{ color: "#2c2c2c", fontSize: "13px", fontFamily: "Effra" }}>
      Creado por:
      <p style={{ color: "#999", margin: "0", fontSize: "13px" }}>{item.creador || "-"}</p>
      <p style={{ color: "#999", margin: "0", fontSize: "13px" }}>{item.fechaCreacion ? new Date(item.fechaCreacion.toDate ? item.fechaCreacion.toDate() : item.fechaCreacion).toLocaleDateString("es-AR") : "-"}</p>
    </div>
  </div>
) : (
  <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}><div><strong style={{ color: "#999", fontSize: "13px", fontFamily: "Effra", fontWeight: "normal", textTransform: "uppercase" }}>{item.id || item.nombre || "-"}</strong></div><div style={{ color: "#2c2c2c", fontSize: "13px", fontFamily: "Effra" }}>Creado por:<p style={{ color: "#999", margin: "0", fontSize: "13px" }}>{item.creador || "-"}</p><p style={{ color: "#999", margin: "0", fontSize: "13px" }}>{item.fechaCreacion ? new Date(item.fechaCreacion.toDate ? item.fechaCreacion.toDate() : item.fechaCreacion).toLocaleDateString("es-AR") : "-"}</p></div></div>
)
}>
{pathId ? (
<a
href={`/trazabilidad/${pathId}`}
target="_blank"
rel="noopener noreferrer"
className="stock-vinos-item"
style={{ textDecoration: "none", display: "inline-block", maxWidth: "140px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", verticalAlign: "middle" }}
onClick={(e) => {
e.preventDefault();
navigate(`/trazabilidad/${pathId}`, { state: { fromStock: true, forceEtiqueta: shouldForceEtiqueta } });
}}
>
{displayId}
</a>
) : (
<span style={{ display: "inline-block", maxWidth: "140px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", verticalAlign: "middle" }}>
{item.nombre || "-"}
</span>
)}
</Tooltip>
</div>
</td>
<td
style={{
cursor: "default",
width: "13.2%",
color: getColorForStockLevel(item.cantidadUnidades, item.existenciasMinimas, item.existenciasMaximas),
}}
>
{item.cantidadUnidades === 0 && item.vino_id && item.vino_id.trim() !== ''
? <span style={{ fontFamily: 'Effra', fontSize: '14px', color: '#e65c5c' }}>VENDIDO</span>
: item.cantidadUnidades
? `${Number(item.cantidadUnidades).toLocaleString("es-AR")} ${unidadesAbreviaturas[item.tipoUnidad] || unidadesAbreviaturas['Unidad']}`
: `0 ${unidadesAbreviaturas[item.tipoUnidad] || unidadesAbreviaturas['Unidad']}`
}
</td>
<td style={{ cursor: "default", width: "10%" }}>
<Tooltip content={<div style={{ display: "flex", flexDirection: "column", gap: "5px", textAlign: "left" }}><div><strong style={{ color: "#2c2c2c", fontSize: "13px", fontFamily: "Effra", fontWeight: "normal", }}>Contado por:</strong><p style={{ margin: 0, fontSize: "13px", color: "#666" }}>{item.ultimoModificador || "-"}</p></div><div><p style={{ margin: 0, fontSize: "13px", color: "#666", textAlign: "left" }}>{item.ultimaActualizacion ? formatearFecha(item.ultimaActualizacion) : "-"}</p></div></div>}>
{(() => {
const { text, estado } = calcularDiasRestantes(item.fechaUltimoConteo, item.diasConteo);
return (<span style={{ color: estado === "atrasado" ? "#e65c5c" : "inherit" }}>{text}</span>);
})()}
</Tooltip>
</td>
<td style={{ cursor: "default", width: "7%" }}>
{item.existenciasMaximas ? (
<>{item.existenciasMaximas} {unidadesAbreviaturas[item.tipoUnidad] || ''}</>
) : (
<>-</>
)}
</td>
<td style={{ cursor: "default", width: "7%" }}>
{item.existenciasMinimas ? (
<>{item.existenciasMinimas} {unidadesAbreviaturas[item.tipoUnidad] || ''}</>
) : (
<>-</>
)}
</td>
<td style={{ cursor: "default", width: "13.2%" }}>
{formatearFechaEntrega(item.ultimaActualizacion ? item.ultimaActualizacion.toDate() : item.fechaCreacion ? item.fechaCreacion.toDate() : null)}
</td>
<td style={{ cursor: "default", width: "13.2%", textAlign:'right' }}>
{item.precioUnidad != null ? `${divisas[item.tipoDivisa] || ""} ${Number(item.precioUnidad).toLocaleString("es-AR")}` : "-"}
</td>
<td style={{ cursor: "default", width: "10%" }}>
{item.descuentoAplicado ? `${item.descuentoAplicado}%` : "-"}
</td>
<td style={{ cursor: "default", width: "13.2%" }}>
{proveedores[item.proveedorAsociado] ? (
<Tooltip offsetX={-80} content={<div style={{ display: "flex", flexDirection: "column", gap: "2px", textAlign: "left" }}><div style={{ display: "flex", flexDirection: "column", gap: "2px", textAlign: "left" }}><strong style={{ color: "#999", fontSize: "13px", fontFamily: "Effra", textAlign: "left", fontWeight: "normal", textTransform: "uppercase" }}>{proveedores[item.proveedorAsociado].razonSocial}</strong><span style={{ color: "#666", fontSize: "13px", textAlign: "left" }}>{proveedores[item.proveedorAsociado].ciudad},{" "}{proveedores[item.proveedorAsociado].pais}</span></div><span style={{ color: "#666", fontSize: "13px", textAlign: "left" }}>{proveedores[item.proveedorAsociado].correoElectronico}</span><span style={{ color: "#666", fontSize: "13px", textAlign: "left" }}>{proveedores[item.proveedorAsociado].telefono}</span></div>}>
<span style={{ display: "inline-block", maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", verticalAlign: "middle" }}>
{proveedores[item.proveedorAsociado].razonSocial}
</span>
</Tooltip>
) : (item.proveedorAsociado || "-")}
</td>
</tr>
);
})}
</tbody>
</table>
</div>
)}
</div>
</div>
))}
</div>
);
};
export default Stock;